// SHARED FOUNDATION — owned by the page skeleton / in-place-view work; FROZEN
// for tab agents.
//
// The FULL customer page (עמוד לקוח) as a host-agnostic component. It is
// rendered by TWO hosts:
//   1. The standalone route /dashboard/contacts/:contactId (ContactDetailPage —
//      a thin wrapper) — used for new-tab opens and shareable deep links.
//   2. IN-PLACE over the contacts list via the `?contact=<id>` search param on
//      /dashboard/contacts (see ContactsPage) — the list stays mounted (hidden)
//      underneath, so "back" is instant and preserves table state + scroll.
//
// This module is intentionally LIGHT at import time: the heavy composition
// (header + 9-tab strip + tab registry + all tabs) is code-split via lazy()
// below. That lets the contacts-table entry points statically import the
// `?contact=` helpers from here without dragging the whole customer-page code
// into the contacts-page chunk (the skeleton's registry stays a dynamic chunk,
// warmed on row hover via preloadContactDetail + preloadContactDetailViewChunks).
import { Suspense, lazy, useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { Box, Card, Stack, Button, Skeleton, Container, useMediaQuery } from "@material-ui/core";
import type { CSSProperties } from "react";
import { useTheme } from "@material-ui/core/styles";
import Page from "components/Page";
import useSettings from "hooks/useSettings";
import EmptyContent from "components/EmptyContent";
import { useLiveContact } from "hooks/useLiveContact";
import { PATH_DASHBOARD } from "routes/paths";
import { preloadContactDetail } from "./data/sessionCache";

const ContactDetailHeader = lazy(() =>
  import("./ContactDetailHeader").then((m) => ({ default: m.ContactDetailHeader }))
);
const ContactDetailTabs = lazy(() =>
  import("./ContactDetailTabs").then((m) => ({ default: m.ContactDetailTabs }))
);

// ---------------------------------------------------------------------------
// ?contact= in-place deep-link helpers (used by ContactsPage + the three
// contacts-table entry points: name cell, row menu, ViewContactDialog).
// ---------------------------------------------------------------------------

/** Search param holding the in-place-opened contact id on /dashboard/contacts. */
export const CONTACT_DETAIL_VIEW_PARAM = "contact";

// Kept in sync with CONTACT_DETAIL_TAB_PARAM in ./ContactDetailTabs — declared
// here too so this light module doesn't statically pull the (lazy) tab strip.
const CONTACT_DETAIL_TAB_PARAM = "tab";

/**
 * Tolerant read of the in-place contact id. Hand-typed deep links may carry a
 * literal '+' (contact ids are international phones) which query-string
 * decoding turns into a space — restore it.
 */
export function readContactDetailViewParam(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get(CONTACT_DETAIL_VIEW_PARAM);
  if (!raw) return null;
  return raw.startsWith(" ") ? `+${raw.slice(1)}` : raw;
}

/**
 * MERGE (never rebuild) the current search params into the "view closed"
 * shape: drops the contact id and the tab deep-link, keeps everything else
 * (table filters, global signature-dialog params, ...).
 */
export function buildCloseContactDetailParams(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(CONTACT_DETAIL_VIEW_PARAM);
  next.delete(CONTACT_DETAIL_TAB_PARAM);
  return next;
}

/**
 * Fire-and-forget warm-up of this view's OWN code chunks (header + tab strip).
 * Complements preloadContactDetail (which warms the tab registry chunk + data
 * caches) — call both on row hover so a click opens with zero chunk fetches.
 */
export function preloadContactDetailViewChunks(): void {
  void import("./ContactDetailHeader").catch(() => undefined);
  void import("./ContactDetailTabs").catch(() => undefined);
}

/**
 * The canonical "open the customer page" action for entry points.
 *
 * - On /dashboard/contacts: opens IN-PLACE by setting `?contact=<id>` with a
 *   history PUSH (not replace) — the browser back button then closes the view.
 *   A stale `?tab=` deep-link is dropped so a fresh open lands on overview.
 * - Anywhere else (calendar, calls, chat, ...): navigates to the standalone
 *   route, exactly like before the in-place pattern existed.
 *
 * Deliberately SUBSCRIPTION-FREE: the URL is read imperatively at click time
 * and navigation goes through the router's stable navigator. useNavigate /
 * useLocation / useSearchParams all subscribe to the location context, which
 * would re-render every entry point (50 table rows × 2 name cells + row
 * menus) on EVERY search-param write while the detail view is open (tab
 * flips, dialog params). NavigationContext's value is stable, so consumers
 * of this hook never re-render on URL changes.
 */
export function useOpenContactDetail(): (contactId: string) => void {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  return useCallback(
    (contactId: string) => {
      // BrowserRouter without basename — window.location mirrors the router.
      const { pathname, search } = window.location;
      // Both list surfaces open the detail IN PLACE. Without /crm here, every row
      // click on the new page would full-page navigate to the standalone route and
      // lose the list's state and scroll.
      if (pathname === PATH_DASHBOARD.general.contacts || pathname === PATH_DASHBOARD.general.crm) {
        const next = new URLSearchParams(search); // MERGE — never rebuild
        next.set(CONTACT_DETAIL_VIEW_PARAM, contactId);
        next.delete(CONTACT_DETAIL_TAB_PARAM);
        const nextSearch = next.toString();
        // history PUSH, intentionally (back closes the view)
        navigator.push({ pathname, search: nextSearch ? `?${nextSearch}` : "" });
      } else {
        navigator.push(PATH_DASHBOARD.general.contactDetail(contactId));
      }
    },
    [navigator]
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

function ContactDetailHeaderSkeleton(): JSX.Element {
  const theme = useTheme();
  // Mirrors ContactDetailHeaderSurface in ContactDetailHeader (kept inline
  // rather than imported — this module must stay light, the real header is a
  // lazy chunk). Without the same flip a card would visibly appear and then
  // vanish on mobile the moment the real header resolves.
  const isMobileSurface = useMediaQuery(theme.breakpoints.down("sm"));

  const body = (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Skeleton variant="circular" width={44} height={44} />
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="text" width={260} height={18} />
      </Box>
      <Skeleton variant="rectangular" width={200} height={36} sx={{ borderRadius: 1 }} />
    </Stack>
  );

  return isMobileSurface ? (
    <Box sx={{ py: 2, borderBlockEnd: `1px solid ${theme.palette.divider}` }}>{body}</Box>
  ) : (
    <Card sx={{ p: 2 }}>{body}</Card>
  );
}

// ---------------------------------------------------------------------------
// Desktop on-page layout constants (design feedback 2026-08: tab content sits
// ON the page and scrolls, while the contact header + tab strip stay pinned).
// ---------------------------------------------------------------------------

// The dashboard's fixed AppBar overlays the scroll container's top 64px
// (MainStyle underlaps it and compensates with padding-top) — sticky elements
// must clear it to stay visible.
const CONTACT_DETAIL_STICKY_TOP = 64;

/**
 * CSS variable exposing the total height pinned above the tab panels (AppBar
 * offset + measured header-and-strip block). Tab content with its OWN sticky
 * elements (the overview reference rail) reads it to compute a top offset
 * that clears the pinned block. Declared here — the single place that knows
 * the pinned geometry.
 */
export const CONTACT_DETAIL_STICKY_OFFSET_VAR = "--contact-detail-sticky-offset";

type ContactDetailViewProps = {
  /** Contact Firestore doc id (route param or ?contact= value). */
  contactId: string | undefined;
  /**
   * Closes the view. In-place host: clears the ?contact= param (replace).
   * Standalone host: navigates back to the contacts list.
   */
  onBack: () => void;
};

export function ContactDetailView({ contactId, onBack }: ContactDetailViewProps): JSX.Element {
  const { themeStretch } = useSettings();
  const theme = useTheme();
  // Surface treatment (mock s4): on desktop the tabs live in a white Card and
  // the tab content area gets a grey surface (painted inside ContactDetailTabs);
  // on mobile the Card chrome is dropped entirely — flat, full-bleed content.
  // The header follows the same convention at the same breakpoint (see
  // ContactDetailHeaderSurface), so the WHOLE page is card-free below sm.
  const isMobileSurface = useMediaQuery(theme.breakpoints.down("sm"));

  // Measured height of the pinned header+strip block (desktop). Feeds the
  // sticky-offset CSS variable the tab panels' own sticky elements read.
  // Callback ref + ResizeObserver: the block lives inside the lazy tabs
  // chunk, so a plain useRef+useEffect could observe a node that doesn't
  // exist yet on first render.
  const [stickyBlockHeight, setStickyBlockHeight] = useState(0);
  const stickyObserverRef = useRef<ResizeObserver | null>(null);
  const stickyBlockRef = useCallback((node: HTMLDivElement | null) => {
    stickyObserverRef.current?.disconnect();
    stickyObserverRef.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setStickyBlockHeight(node.offsetHeight));
    observer.observe(node);
    setStickyBlockHeight(node.offsetHeight);
    stickyObserverRef.current = observer;
  }, []);

  // The contact is its LIVE DOCUMENT, shared with an already-open sidebar: one read, kept
  // fresh under edits made elsewhere (another tab, mobile, a server webhook), and
  // authoritative when it says the document is gone.
  //
  // Nothing here reads `useLegacyContactsBook().getAllContacts()`. Calling that getter is what downloads
  // the caller's whole book, and this page needs one document of it.
  const { contact, loading: liveLoading } = useLiveContact(contactId);

  // Cold links resolve from the first snapshot; a sidebar handoff is already live.
  const stillResolving = !contact && liveLoading;

  // Warm every tab's session cache (and the tabs code chunk) as soon as the
  // contact is known — deep links get the same warm open as table-row hovers.
  useEffect(() => {
    if (contact) preloadContactDetail(contact);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id]);

  // Kick this view's own chunk fetches immediately (in parallel with contact
  // resolution) — matters on cold deep links where no hover pre-warmed them.
  useEffect(() => {
    preloadContactDetailViewChunks();
  }, []);

  // Escape closes the view. Skipped when the key press is aimed at a portal
  // (an open MUI dialog/menu owns its Escape) or at an editable field.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      const inPortal =
        target && target !== document.body && !rootRef.current?.contains(target);
      if (inPortal) return;
      if (
        target &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      onBack();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onBack]);

  const fullName = contact
    ? [contact.callerFirstName, contact.callerLastName].filter(Boolean).join(" ").trim() ||
      contact.id
    : null;

  return (
    <Page ref={rootRef} title={fullName ? `Webot - ${fullName}` : "Webot - עמוד לקוח"}>
      <Container sx={{ pb: 16 }} maxWidth={themeStretch ? false : "lg"}>
        {contact ? (
          <Suspense fallback={<ContactDetailHeaderSkeleton />}>
            {isMobileSurface ? (
              // No top margin on purpose: the header now ends in a hairline
              // divider (its flat-surface separator) and the chip grid opens
              // with pt:2, so the rule sits symmetrically between 16px of
              // header padding above and 16px of strip padding below — the
              // same rhythm the divider-separated TabCard sections use. An
              // extra mt here would push the strip off that rhythm and make
              // the rule read as floating.
              <>
                <ContactDetailHeader contact={contact} onBack={onBack} />
                <Box>
                  <ContactDetailTabs contact={contact} />
                </Box>
              </>
            ) : (
              // Desktop on-page layout (design feedback 2026-08): the tab
              // panels' cards float directly on the page and scroll with it,
              // while the contact header + the tab strip stay PINNED below the
              // fixed AppBar. One sticky block holds both (no per-element
              // offset math); its wrapper is opaque so scrolling content never
              // shows through the gaps between the two cards. The measured
              // block height is published as a CSS variable for tab-internal
              // sticky elements (the overview reference rail).
              <ContactDetailTabs
                contact={contact}
                renderLayout={(strip, panels) => (
                  <>
                    <Box
                      ref={stickyBlockRef}
                      sx={{
                        position: "sticky",
                        top: CONTACT_DETAIL_STICKY_TOP,
                        zIndex: (t) => t.zIndex.appBar - 2,
                        bgcolor: "background.default",
                        // A real opaque band below the strip: scrolling content
                        // emerges 16px under the pinned block instead of being
                        // sliced flush against the tabs.
                        pb: 2
                      }}
                    >
                      <ContactDetailHeader contact={contact} onBack={onBack} />
                      <Card sx={{ mt: 1.5 }}>{strip}</Card>
                    </Box>
                    <Box
                      style={
                        {
                          [CONTACT_DETAIL_STICKY_OFFSET_VAR]: `${
                            CONTACT_DETAIL_STICKY_TOP + stickyBlockHeight
                          }px`
                        } as CSSProperties
                      }
                    >
                      {panels}
                    </Box>
                  </>
                )}
              />
            )}
          </Suspense>
        ) : stillResolving ? (
          <ContactDetailHeaderSkeleton />
        ) : (
          <Stack alignItems="center" spacing={1}>
            <EmptyContent
              title="הלקוח לא נמצא"
              description="ייתכן שהלקוח נמחק או שהקישור שגוי. חזרו לרשימת הלקוחות ונסו שוב."
            />
            <Button variant="outlined" color="inherit" onClick={onBack}>
              חזרה לרשימת הלקוחות
            </Button>
          </Stack>
        )}
      </Container>
    </Page>
  );
}
